import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  DEFAULT_KEY_NUTRIENTS_CSV,
  FRAME_IMAGE,
  KEY_NUTRIENT_OPTIONS,
  PATIENT_FLAG_DEFS,
  PRIMARY_MACRO_OPTIONS,
  SAFE_MODE_FLAGS,
  clinicDefaultsFor,
  type PatientFlagDef,
  type PatientFlagKey,
  type PatientFlagsState,
} from '@/lib/patientFlags';
import type { ClinicResponse } from '@/api/hospital';

/**
 * Per-patient app-flag overrides — the "App Settings" panel in v1's
 * Add Patient modal. Each row is a select with three states:
 *   - "Clinic default (…)"  → flag stays unset; server uses clinic value
 *   - explicit on / off      → flag is sent in `flags_raw` on submit
 *
 * The right column shows a phone mockup that updates on hover or change
 * to give the clinician a preview of what the patient will see.
 *
 * Visibility of each row is gated by clinic-level "allow toggle" flags
 * (e.g. `clinician_patient_allow_cal_carb_toggle`) — clinics that don't
 * permit per-patient overrides for a given setting hide that row.
 */

type Props = {
  clinic: ClinicResponse | undefined;
  /** All clinician-level flags so we can read the visibility gates. */
  clinicianFlags: Record<string, unknown> | undefined;
  /** Current patient-flag overrides (controlled by the parent form). */
  value: PatientFlagsState;
  onChange: (next: PatientFlagsState) => void;
};

const isOn = (v: unknown): boolean => v === true || v === 'true';

const renderClinicDefault = (raw: unknown): string => {
  if (raw === undefined || raw === null || raw === '') return '—';
  if (typeof raw === 'boolean') return raw ? 'on' : 'off';
  if (typeof raw === 'number') return String(raw);
  return String(raw);
};

export const PatientFlagsSection = ({ clinic, clinicianFlags, value, onChange }: Props) => {
  const { t } = useTranslation();

  const clinicDefaults = useMemo(() => clinicDefaultsFor(clinic?.flags), [clinic?.flags]);
  const showTotalCarbs =
    Boolean(value.patient_show_total_carbs ?? clinic?.flags?.patient_show_total_carbs);

  // Track which row is currently being hovered so the preview image
  // matches the row the clinician is reading. Falls back to the
  // currently-resolved value of the first row otherwise.
  const [hovered, setHovered] = useState<PatientFlagKey | null>(null);

  // Filter rows by clinic-level visibility gate. When the gate flag is
  // missing, the row stays hidden — matches v1.
  const visibleRows = useMemo(
    () =>
      PATIENT_FLAG_DEFS.filter((d) => {
        if (d.visibleWhen === null) return true;
        return clinicianFlags?.[d.visibleWhen] === true;
      }),
    [clinicianFlags],
  );

  // Whether the macro-summary card is currently enabled (per-patient
  // value wins, otherwise fall back to the clinic default). Drives both
  // the visibility of the `patient_app_key_nutrients` row AND its
  // visual highlight — when the summary is on, the nutrient picker is
  // the active follow-up affordance and we tint it so clinicians notice
  // the connection.
  const macroSummaryOn =
    isOn(value.patient_app_show_macro_nutrition_card)
    || (value.patient_app_show_macro_nutrition_card === undefined
      && Boolean(clinic?.flags?.patient_app_show_macro_nutrition_card));

  // Special "Macronutrients to show" row only renders when the macro
  // summary itself is enabled (matches v1's nested visibility).
  const filteredRows = useMemo(
    () =>
      visibleRows.filter((d) => {
        if (d.key !== 'patient_app_key_nutrients') return true;
        return macroSummaryOn;
      }),
    [visibleRows, macroSummaryOn],
  );

  // ------ Safe mode ------
  // Match v1: ON sets a fixed bundle of flags to off/empty; toggling off
  // clears those overrides (returns them to "use clinic default").
  const [safeMode, setSafeMode] = useState<'unset' | 'on' | 'off'>('unset');
  const onSafeModeChange = (next: 'on' | 'off') => {
    setSafeMode(next);
    if (next === 'on') {
      onChange({ ...value, ...SAFE_MODE_FLAGS });
    } else {
      const cleared = { ...value };
      for (const k of Object.keys(SAFE_MODE_FLAGS) as PatientFlagKey[]) {
        delete cleared[k];
      }
      onChange(cleared);
    }
  };
  const safeModeRowsHidden = safeMode === 'on';
  const renderedRows = filteredRows.filter((d) => !(safeModeRowsHidden && d.hideInSafeMode));

  // Resolve the image to show: either the row currently hovered, or the
  // primary macro row's resolved value (since that's the most visually
  // distinctive). Falls back to the empty frame.
  const imageDef =
    renderedRows.find((d) => d.key === hovered)
      ?? renderedRows.find((d) => d.key === 'patient_app_primary_macronutrient')
      ?? renderedRows[0];
  const resolvedForImage = imageDef
    ? value[imageDef.key] ?? clinic?.flags?.[imageDef.key]
    : undefined;
  const previewImage = imageDef ? imageDef.image(resolvedForImage, { showTotalCarbs }) : FRAME_IMAGE;

  // Hide whole panel if there's nothing the clinician can override.
  if (visibleRows.length === 0) return null;

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('App settings')}
        </p>
        <SafeModeToggle value={safeMode} onChange={onSafeModeChange} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-3">
          {renderedRows.map((def) => (
            <FlagRow
              key={def.key}
              def={def}
              value={value}
              clinicDefault={clinicDefaults[def.key]}
              // Tint the nutrient-picker row when the macro summary is
              // currently on (either per-patient override or clinic
              // default). Signals to the clinician that this row is
              // the active follow-up affordance to the toggle above.
              highlight={def.key === 'patient_app_key_nutrients' && macroSummaryOn}
              onHover={() => setHovered(def.key)}
              onChange={(patch) => {
                let merged: PatientFlagsState = { ...value, ...patch };
                // When the macro-summary toggle flips ON and the
                // per-patient key_nutrients value is still empty/unset,
                // pre-fill the default 4 macros so the row below renders
                // useful chips instead of an empty selector. The
                // clinician can still trim or add nutrients afterward.
                if (
                  patch.patient_app_show_macro_nutrition_card === true
                  && !merged.patient_app_key_nutrients
                ) {
                  merged = {
                    ...merged,
                    patient_app_key_nutrients: DEFAULT_KEY_NUTRIENTS_CSV,
                  };
                }
                onChange(merged);
              }}
            />
          ))}
        </div>

        <div className="hidden lg:block">
          <div className="sticky top-2 flex items-start justify-center">
            <img
              src={previewImage}
              alt={t('App preview')}
              className="h-auto w-full max-w-[260px] rounded-md object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SafeModeToggle = ({
  value,
  onChange,
}: {
  value: 'unset' | 'on' | 'off';
  onChange: (next: 'on' | 'off') => void;
}) => {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>{t('Safe mode')}</span>
      {/* Two-state On/Off. The initial 'unset' (never touched) reads as Off,
          and the control only ever emits 'on' | 'off'. */}
      <select
        value={value === 'on' ? 'on' : 'off'}
        onChange={(e) => onChange(e.target.value as 'on' | 'off')}
        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
      >
        <option value="off">{t('Off')}</option>
        <option value="on">{t('On')}</option>
      </select>
    </label>
  );
};

const FlagRow = ({
  def,
  value,
  clinicDefault,
  highlight,
  onHover,
  onChange,
}: {
  def: PatientFlagDef;
  value: PatientFlagsState;
  clinicDefault: unknown;
  /** When true, the row renders with a tinted background + border so
   *  it reads as the "active" follow-up to a parent toggle. Used by
   *  the macro-summary → key-nutrients pair. */
  highlight?: boolean;
  onHover: () => void;
  onChange: (patch: PatientFlagsState) => void;
}) => {
  const { t } = useTranslation();
  const current = value[def.key];

  // Choose the right control renderer.
  let control: React.ReactNode;
  if (def.control.kind === 'boolean') {
    const selectValue = current === undefined ? 'unset' : isOn(current) ? 'true' : 'false';
    control = (
      <select
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value;
          if (next === 'unset') {
            const copy = { ...value };
            delete copy[def.key];
            onChange(copy);
            return;
          }
          onChange({ [def.key]: next === 'true' });
        }}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="unset">{t('Clinic default ({{ defaultValue }})', { defaultValue: renderClinicDefault(clinicDefault) })}</option>
        <option value="true">{t(def.control.onLabel)}</option>
        <option value="false">{t(def.control.offLabel)}</option>
      </select>
    );
  } else if (def.control.kind === 'primaryMacro') {
    const selectValue =
      current === undefined ? 'unset' : current === '' ? 'none' : (current as string);
    control = (
      <select
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value;
          if (next === 'unset') {
            const copy = { ...value };
            delete copy[def.key];
            onChange(copy);
            return;
          }
          onChange({ [def.key]: next === 'none' ? '' : next });
        }}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="unset">
          {t('Clinic default ({{ defaultValue }})', { defaultValue: renderClinicDefault(clinicDefault) })}
        </option>
        {PRIMARY_MACRO_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {t(o.label)}
          </option>
        ))}
      </select>
    );
  } else if (def.control.kind === 'hideMealHours') {
    const numericValue = Number(current);
    const status = current === undefined ? 'unset' : numericValue > 0 ? 'on' : 'off';
    control = (
      <div className="space-y-2">
        <select
          value={status}
          onChange={(e) => {
            const next = e.target.value;
            if (next === 'unset') {
              const copy = { ...value };
              delete copy[def.key];
              onChange(copy);
              return;
            }
            if (next === 'off') {
              onChange({ [def.key]: '0' });
              return;
            }
            // turning on — preserve previous numeric or default to 12.
            const prior = numericValue > 0 ? numericValue : 12;
            onChange({ [def.key]: String(prior) });
          }}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="unset">
            {t('Clinic default ({{ defaultValue }})', { defaultValue: renderClinicDefault(clinicDefault) })}
          </option>
          <option value="on">{t('Hide meals after X hours (enter a number)')}</option>
          <option value="off">{t('Off')}</option>
        </select>
        {status === 'on' && (
          <div className="flex items-end gap-2">
            <Input
              type="number"
              min={0}
              value={String(numericValue || 0)}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n >= 0) onChange({ [def.key]: String(n) });
              }}
              className="w-24"
            />
            <Label className="pb-2 text-xs text-muted-foreground">{t('hours')}</Label>
          </div>
        )}
      </div>
    );
  } else if (def.control.kind === 'keyNutrients') {
    const selectedCsv = (current as string | undefined) ?? '';
    const selected = new Set(
      selectedCsv
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    const toggle = (n: string) => {
      const next = new Set(selected);
      if (next.has(n)) next.delete(n);
      else if (next.size < 4) next.add(n);
      onChange({ [def.key]: Array.from(next).join(', ') });
    };
    control = (
      <div className="flex flex-wrap gap-1">
        {KEY_NUTRIENT_OPTIONS.map((o) => {
          const active = selected.has(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input text-muted-foreground hover:bg-muted',
              )}
            >
              {t(o.label)}
            </button>
          );
        })}
      </div>
    );
  } else {
    control = null;
  }

  return (
    <div
      className={cn(
        'space-y-1 rounded-md p-2 transition-colors',
        highlight
          ? 'border border-primary/40 bg-primary/5'
          : 'hover:bg-muted/40',
      )}
      onMouseEnter={onHover}
      onFocus={onHover}
    >
      <Label>{t(def.label)}</Label>
      {control}
      {def.description && (
        <p className="text-xs text-muted-foreground">{t(def.description)}</p>
      )}
    </div>
  );
};
